<template>
  <div id="file-drag-drop">
    <form
      v-show="!files.length"
      id="drop-form"
      @drop="handleFileDrop( $event )"
    >
      <span class="drop-files">Drop the files here!</span>
    </form>
    <form
      v-show="files.length"
      id="slider-drop"
      @drop="handleFileDrop( $event )"
    >
      <v-item-group multiple>
        <v-container>
          <v-row :class="{'drag-disabled': !editing && !initialFiles}">
            <draggable
              v-model="files"
              class="wrapper-drag"
              tag="div"
              v-bind="dragOptions"
              @start="isDragging = true"
              @end="reOrder($event)"
            >
              <transition-group
                type="transition"
                name="flip-list"
              >
                <v-col
                  v-for="(file, key) in files"
                  :key="`drag-${key}`"
                  cols="12"
                  md="4"
                  class="list-group-item"
                >
                  <v-item>
                    <v-card
                      class="d-flex align-center img-container"
                      dark
                      height="190"
                    >
                      <v-icon
                        v-if="files.length"
                        dark
                        :class="{'alternate-icon-small': !mdiClose}"
                        class="mx-auto btn-remove"
                        @click="removeFile(key)"
                      >
                        {{ mdiClose }}
                      </v-icon>
                      <template v-if="isVideoFile(file)">
                        <video
                          :src="previewSources[key]"
                          class="preview drag-and-drop-preview video-preview"
                          controls
                        />
                      </template>
                      <template v-else>
                        <img
                          :src="previewSources[key] || ''"
                          class="preview drag-and-drop-preview"
                          :class="`rotate_images__position_${rotatePosition.length ? rotatePosition[key] : ''}`"
                        />
                      </template>
                      <v-icon
                        dark
                        class="mx-auto btn-rotate"
                        @click="rotateImage(key)"
                      >
                        {{ mdiCropRotate }}
                      </v-icon>
                    </v-card>
                  </v-item>
                </v-col>
              </transition-group>
            </draggable>
          </v-row>
        </v-container>
      </v-item-group>
    </form>

    <v-row
      class="wrapper-action-buttons"
      justify="center"
      align="center"
      no-gutters
    >
      <v-col
        cols="12"
        sm="auto"
        class="d-flex justify-center"
      >
        <label>
          <input
            type="file"
            class="file-uploader"
            multiple
            @change="handleFileUpload( $event )"
          />
          <v-btn
            color="primary"
            class="mt-5 alight-center"
            @click="callFileUploader()"
          >
            {{ relatedFiles ? 'Relate more images&nbsp;&nbsp;' : 'Click to upload&nbsp;&nbsp;' }}
            <v-icon
              right
              dark
              :class="{'alternate-icon-small': !mdiCloudUpload}"
              class="mx-auto"
            >
              {{ mdiCloudUpload }}
            </v-icon>
          </v-btn>
        </label>
        <v-tooltip
          v-if="!$vuetify.breakpoint.mdAndDown"
          top
          color="#000000"
          max-width="200px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              :class="{'alternate-icon-small': !mdiInformationOutline}"
              class="mx-auto icon-relate"
              title="Relate more images?"
              v-bind="attrs"
              v-on="on"
            >
              {{ mdiInformationOutline }}
            </v-icon>
          </template>
          <span>Click to upload more pictures on drag into the gray box...</span>
        </v-tooltip>
      </v-col>
      <v-col
        v-if="initialFiles || $store.state.diary || $store.state.modal_edit || $store.state.edit_pages || $store.state.modal_upload"
        cols="12"
        sm="auto"
        class="d-flex justify-center ml-sm-3"
      >
        <v-btn
          color="primary"
          class="mt-5"
          @click="callGallery()"
        >
          Choose from Gallery&nbsp;&nbsp;
          <v-icon
            dark
            :class="{'alternate-icon-small': !mdiImageSearch}"
            class="mx-auto"
          >
            {{ mdiImageSearch }}
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import heic2any from 'heic2any'
import exifr from 'exifr'
import {
  mdiCloudUpload, mdiClose, mdiInformationOutline, mdiImageSearch, mdiCropRotate,
} from '@mdi/js'

export default {
  components: {
    draggable,
  },
  props: ['relatedFiles', 'file', 'editing', 'rotatePositionProp', 'initialFiles'],
  data() {
    return {
      dragAndDropCapable: false,
      files: [],
      previewSources: [],
      videoObjectUrls: [],
      uploadPercentage: 0,
      resultImage: '',
      mimeType: null,
      mdiCloudUpload,
      mdiClose,
      mdiInformationOutline,
      mdiImageSearch,
      mdiCropRotate,
      isDragging: false,
      rotatePosition: [],
    }
  },
  computed: {
    fileStatus() {
      return this.$store.state.resetUpload
    },
    galleryItems() {
      return this.$store.state.galleryItems
    },
    dragOptions() {
      return {
        animation: 1,
        group: 'description',
        disabled: !this.editing && !this.initialFiles,
        ghostClass: 'ghost',
      }
    },
  },
  watch: {
    galleryItems(val) {
      if (val) {
        val.forEach(value => {
          this.files.push(value)
        })
        this.getImagePreviews()
      }
    },
    fileStatus(value) {
      if (value) {
        this.removeFile(0)
      }
    },
  },
  beforeMount() {
    if (!this.rotatePositionProp) {
      this.rotatePosition = []
    } else {
      this.rotatePosition = this.rotatePositionProp
    }
  },
  mounted() {
    if (this.initialFiles) {
      this.files = [...this.initialFiles]
      this.getImagePreviews()
    }
    if (this.editing) {
      this.files = this.relatedFiles
      this.getImagePreviews()
    }

    /*
          Determine if drag and drop functionality is capable in the browser
      */
    this.dragAndDropCapable = this.determineDragAndDropCapable()

    /*
          If drag and drop capable, then we continue to bind events to our elements.
      */
    if (this.dragAndDropCapable) {
      this.bindEvents()
    }
  },
  beforeDestroy() {
    this.revokeVideoObjectUrls()
  },

  methods: {
    isVideoFile(file) {
      if (!file) return false
      const mime = file.type || ''
      const name = file.name || ''

      return mime.startsWith('video/') || /\.(mov|mpg|mpeg|mp4|wmv|avi|webm)$/i.test(name)
    },
    isImageFile(file) {
      if (!file) return false
      const mime = file.type || ''
      const name = file.name || ''

      return mime.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff|svg)$/i.test(name)
    },
    rotateImage(key) {
      const position = parseInt(this.rotatePosition[key], 10) === 3 ? 0 : parseInt(this.rotatePosition[key], 10) + 1
      this.$set(this.rotatePosition, key, position)
      this.$emit('rotation_position', [...this.rotatePosition])
    },
    reOrder(event) {
      if (event && event.oldIndex !== event.newIndex) {
        const [rotation] = this.rotatePosition.splice(event.oldIndex, 1)
        this.rotatePosition.splice(event.newIndex, 0, rotation || 0)
      }
      this.isDragging = false
      this.getImagePreviews()
    },
    callGallery() {
      this.$store.commit('showGallery', {
        open: true,
        list: true,
      })
    },
    callFileUploader() {
      document.querySelector('.file-uploader').click()
    },
    handleFileUpload(event) {
      for (let i = 0; i < event.target.files.length; i++) {
        if (event.target.files[i]) {
          this.files.push(event.target.files[i])
        }
      }
      this.getImagePreviews()
    },
    bindEvents() {
    /*
              Listen to all of the drag events and bind an event listener to each
              for the fileform.
          */
      ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(evt => {
      /*
                For each event add an event listener that prevents the default action
                (opening the file in the browser) and stop the propagation of the event (so
                no other elements open the file in the browser)
            */
        document.getElementById('drop-form').addEventListener(evt, e => {
          e.preventDefault()
          e.stopPropagation()
        }, false)
        document.getElementById('slider-drop').addEventListener(evt, e => {
          if (evt !== 'dragstart') {
            e.preventDefault()
            e.stopPropagation()
          }
        }, false)
      })
    },

    handleFileDrop(event) {
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        this.files.push(event.dataTransfer.files[i])
      }
      this.getImagePreviews()
    },

    determineDragAndDropCapable() {
    /*
            Create a test element to see if certain events
            are present that let us do drag and drop.
        */
      const div = document.createElement('div')

      /*
            Check to see if the `draggable` event is in the element
            or the `ondragstart` and `ondrop` events are in the element. If
            they are, then we have what we need for dragging and dropping files.

            We also check to see if the window has `FormData` and `FileReader` objects
            present so we can do our AJAX uploading
        */
      return (('draggable' in div)
                        || ('ondragstart' in div && 'ondrop' in div))
                        && 'FormData' in window
                        && 'FileReader' in window
    },
    revokeVideoObjectUrls() {
      this.videoObjectUrls.forEach(url => {
        try {
          URL.revokeObjectURL(url)
        } catch (error) {
          console.warn('Failed to revoke video preview URL', error)
        }
      })
      this.videoObjectUrls = []
    },
    async getImagePreviews() {
      const arrayEmit = new Array(this.files.length)
      this.revokeVideoObjectUrls()
      this.previewSources = []
      const tasks = this.files.map(async (file, i) => {
        if (typeof file === 'object' && file !== null) {
          if (this.isImageFile(file)) {
            let fileToRead = file
            let mimeType = file.type

            if (/\.(heic|heif)$/i.test(file.name || '') || /^image\/hei[cf](?:-sequence)?$/i.test(file.type || '')) {
              try {
                const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 })
                fileToRead = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob
                if (!fileToRead) throw new Error('No image was returned by HEIC conversion')
                mimeType = 'image/jpeg'
              } catch (error) {
                console.warn('HEIC conversion failed', error)
              }
            }

            const result = await new Promise(resolve => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.readAsDataURL(fileToRead)
            })

            this.$set(this.previewSources, i, result)
            this.$set(this.rotatePosition, i, this.rotatePosition[i] || 0)
            arrayEmit[i] = { name: file.name, file: result, mimeType }
          } else if (this.isVideoFile(file)) {
            const previewSource = URL.createObjectURL(file)
            this.videoObjectUrls.push(previewSource)

            const result = await new Promise(resolve => {
              const reader = new FileReader()
              reader.onload = () => resolve(reader.result)
              reader.readAsDataURL(file)
            })

            this.$set(this.previewSources, i, previewSource)
            this.$set(this.rotatePosition, i, this.rotatePosition[i] || 0)
            arrayEmit[i] = { name: file.name, file: result, mimeType: file.type }
          }
        } else {
          this.$set(this.previewSources, i, file)
          this.$set(this.rotatePosition, i, this.rotatePosition[i] ? this.rotatePosition[i] : 0)
          arrayEmit[i] = file
        }
      })

      await Promise.all(tasks)
      this.$emit('filed', arrayEmit.filter(Boolean))
      this.$emit('rotation_position', this.rotatePosition)

      // Extract EXIF metadata from the first image File object only.
      const firstImageFile = this.files.find(file => file instanceof File && this.isImageFile(file))
      if (firstImageFile) {
        this.extractMetadata(firstImageFile)
      } else {
        this.$emit('metadata', {
          date: null,
          latitude: null,
          longitude: null,
        })
      }
    },
    async extractMetadata(file) {
      if (!this.isImageFile(file)) {
        return
      }

      try {
        console.log('[EXIF] Extracting metadata from:', file.name, 'type:', file.type, 'size:', file.size)

        // First, parse everything to see all available tags
        const allExif = await exifr.parse(file)
        console.log('[EXIF] All tags:', allExif)

        // Parse with GPS enabled
        const gps = await exifr.gps(file)
        console.log('[EXIF] GPS result:', gps)

        if (!allExif && !gps) {
          console.warn('[EXIF] No metadata found in file')
          return
        }

        const date = allExif ? (allExif.DateTimeOriginal || allExif.CreateDate || allExif.ModifyDate || null) : null
        const latitude = gps ? gps.latitude : null
        const longitude = gps ? gps.longitude : null

        console.log('[EXIF] Extracted date:', date)
        console.log('[EXIF] Extracted location:', latitude, longitude)

        const metadata = {
          date: date,
          latitude: latitude,
          longitude: longitude,
        }
        this.$emit('metadata', metadata)
      } catch (e) {
        console.warn('[EXIF] Error extracting metadata:', e)
      }
    },
    removeFile(key) {
      this.files.splice(key, 1)
      this.rotatePosition.splice(key, 1)
      this.getImagePreviews()
    },
  },
}
</script>

<style scoped lang="scss">
form {
  display: block;
  width: 100%;
  background: #ccc;
  margin: auto;
  text-align: center;
  max-height: 430px;
  border-radius: 4px;
  height: 50vh;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  overflow-y: scroll;

  @media(max-width: 940px) {
    max-height: 250px;
  }

  .drop-files {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

@media(max-width: 940px) {
  .v-card {
    .v-btn  {
      display: block;
      margin: 0 auto;
    }
  }
}

div.file-listing {
  width: 100%;
  margin: auto;
  padding: 10px;
  height: 100vh;
  max-height: 500px;
}

div.file-listing img{
  width: auto;
  height: 95%;
  display: block;
  margin: 0 auto;
}

div.remove-container{
  text-align: center;
}

div.remove-container a{
  color: red;
  cursor: pointer;
}

a.submit-button{
  display: block;
  margin: auto;
  text-align: center;
  width: 200px;
  padding: 10px;
  text-transform: uppercase;
  background-color: #CCC;
  color: white;
  font-weight: bold;
  margin-top: 20px;
}

progress{
  width: 100%;
  margin: auto;
  display: block;
  margin-top: 20px;
  margin-bottom: 20px;
}

input[type="file"] {
  opacity: 0;
  position: absolute;
  left: -999999px;
}
.v-item-group {
  overflow: hidden;
  overflow-y: auto;
  height: 420px;
  .wrapper-image {
    padding: 5px 2.5px;
  }

  .img-container {
    overflow: hidden;
    border-radius: 5px;
    min-height: 150px;
    height: 190px;
    min-width: 200px;
    max-width: 200px;
    aspect-ratio: 16 / 9;
  }

  .img-container img,
  .img-container video,
  .video-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
    min-width: 250px;
    min-height: 140px;
  }

  img {
    width: 100%;
    height: auto;
  }
}

.btn-remove, .chk-images,
.btn-rotate {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  padding: 0px;
  background: rgba(0,0,0,0.8);
  border-radius: 100% !important;
}

.btn-rotate {
  right: auto;
  left: 50%;
  top: auto;
  bottom: 5px;
  transform: translateX(-50%);
  padding: 5px;
}

.chk-images {
  right: auto;
  left: 10px;
  background: none;
  top: 50% !important;
  transform: translateY(-50%)
}

.icon-relate {
  position: relative;
  top: 11px;
  left: 5px;
}

.wrapper-drag {
  > span {
    display: flex;
    flex-wrap: wrap;
    flex: 1 1 auto;
    margin: 12px;
      gap: 12px;
      justify-content: flex-start;
    }

    .list-group-item {
      padding: 6px;
      min-width: 200px;
      max-width: 200px;
      box-sizing: border-box;
    }

    .item {
      padding: 0;
      margin: 0;
      border-width: thin;
      position: relative;
      cursor: move;
      display: flex;
      align-items: center;
      border-color: #312d4b;
      color: rgba(231, 227, 252, 0.68);
      background-color: #312d4b;
      overflow: hidden;
      border-radius: 5px;
      box-shadow: 0 2px 10px 0 rgba(94, 86, 105, 0.1);
      height: 190px;
      flex: 0 0 auto;
      width: 100%;
      min-width: 200px;
      max-width: 100%;

      img {
        line-height: 1.5;
        text-align: center;
        overflow-wrap: break-word;
        cursor: pointer;
        width: 100%;
        height: auto;
        display: block;
      }
    }
  }

.list-group-item i {
  cursor: pointer;
}
</style>
